import api from "./api";

// Member data layer — wraps the existing backend endpoints
// (see SFMIS-Backend/routes/web.php). The show endpoint wraps its single
// result in an outer array ([[ … ]]); it is unwrapped here so callers get a
// plain object.

// GET /api/members — admin listing (permission:view_members). Optional
// { search, member_type_id, member_status_id, payment_status_id,
// card_status_id, club_id, requested_club_id, member_gender_id } filters.
// Returns a plain array of formatted Member objects.
export const listMembers = (params = {}) =>
  api.get("/api/members", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/members/{id} → full member record (+ registration process rows).
export const getMember = (id) =>
  api.get(`/api/members/${id}`).then((r) => (Array.isArray(r.data) ? r.data[0] : r.data));

// POST /api/staff/member-registrations — staff-desk registration: a system
// user starts a PENDING member_registration_process (permission:
// create_staff_member_registrations) on a walk-in member's behalf, with no
// OTP gate. Same multipart payload shape and validation as the public
// registerMember()/previewMemberRegistration() in authService.js (title,
// initials, name_denoted_by_initials, lastname, member_gender_id, email,
// nic, primary_phone, secondary_phone, date_of_birth, address,
// membership_type: "independent"|"club", club_id (required if "club"),
// photo (required)) — just without the email-OTP requirement. Returns
// { process_id, payable_amount, payment_reference, ... } like the public
// flow; the member is only finalized once payment clears (shroff voucher +
// the existing "Confirm Payment" tab's manual-confirm, the same
// submit/approve queue public counter-payments already use) — unlike
// createMemberLegacy(), which finalizes in one step together with the
// payment record.
// POST /api/staff/member-registrations/preview — no-write dry run of
// createStaffMemberRegistration() below (same permission, same multipart
// payload, same backend validation). Response:
// { valid, message, data: { ...validated (normalized) fields, photo:
// { original_name, mime_type, size_bytes }, income_id, income_description,
// ledger_code, payable_amount } } — returns the inner `data` object.
// Validation failures come back as a 422 with `errors`, like the create route.
export const previewStaffMemberRegistration = (formData) =>
  api.post("/api/staff/member-registrations/preview", formData).then((r) => r.data?.data ?? r.data);

export const createStaffMemberRegistration = (formData) =>
  api.post("/api/staff/member-registrations", formData).then((r) => {
    let data = r.data;
    while (Array.isArray(data)) data = data[0];
    return data;
  });

// GET /api/members/expired — admin listing (permission:view_members, same
// gate as listMembers above — anyone who can reach this page already has
// it). Matches members whose member_status is "Expired" OR whose
// membership_end_date has already passed (covers rows that expired but
// haven't had their status flag flipped yet). Optional
// { search, member_type_id, club_id } filters. Response:
// { as_of, count, members: [...] } — same formatted Member shape as
// listMembers, not a plain array, so callers read `.members`.
export const listExpiredMembers = (params = {}) =>
  api
    .get("/api/members/expired", { params })
    .then((r) => ({ as_of: r.data?.as_of ?? null, count: r.data?.count ?? 0, members: Array.isArray(r.data?.members) ? r.data.members : [] }));

// ── Lookups owned by MemberController (all public, unauthenticated) ────────
// Used to populate filter dropdowns and the member edit form.
export const listMemberGenders = () =>
  api.get("/api/member-genders").then((r) => (Array.isArray(r.data) ? r.data : []));

export const listMemberTitles = () =>
  api.get("/api/member-titles").then((r) => (Array.isArray(r.data) ? r.data : []));

export const listMemberStatuses = () =>
  api.get("/api/member-statuses").then((r) => (Array.isArray(r.data) ? r.data : []));

export const listPaymentStatuses = () =>
  api.get("/api/payment-statuses").then((r) => (Array.isArray(r.data) ? r.data : []));

export const listCardStatuses = () =>
  api.get("/api/card-statuses").then((r) => (Array.isArray(r.data) ? r.data : []));

// POST /api/members/legacy → admin-created member record (permission:
// create_legacy_members), used by the back-office Members page's "Add Member" flow
// to register a member directly instead of going through the public
// email/OTP registration flow. Multipart FormData (photo upload). Fields:
// title, initials, name_denoted_by_initials, lastname, member_gender_id,
// date_of_birth, address, email, nic_number, primary_phone_number,
// secondary_phone_number, member_type_id, membership_start_date,
// membership_end_date, photo — plus club_id, required only for club-type
// member types (omit it for independent members). Also carries the
// counter-payment recorded on the form's second ("Payment Details") step:
// income_id, amount_paid, paid_date, slip_reference, and optional note. The
// response wraps the created record in an outer array like show/update;
// unwrapped here.
export const createMemberLegacy = (formData) =>
  api.post("/api/members/legacy", formData).then((r) => (Array.isArray(r.data) ? r.data[0] : r.data));

// PUT /api/members/{id} → admin update of any member's editable personal
// details (title, names, gender, NIC, phones, DOB, address, photo). Admin-
// scoped (permission:edit_members) — used by the back-office Members
// Management page. It's a partial update: only the fields present in the
// FormData are changed. The response wraps the updated record in an outer
// array (like show); it is unwrapped here so callers get a plain object.
export const updateMember = (id, formData) =>
  api
    .put(`/api/members/${id}`, formData)
    .then((r) => (Array.isArray(r.data) ? r.data[0] : r.data));

// PUT /api/member/me → self-service update of the logged-in member's own
// editable personal details. Member-scoped (permission:edit_own_profile) —
// the id is resolved server-side from the bearer token, unlike the admin
// route above which a member token can't reach. Same partial-update
// semantics; used by the member/coach/independent dashboards. The request
// interceptor in api.js attaches the member bearer token automatically.
export const updateOwnMemberProfile = (formData) =>
  api
    .put("/api/member/me", formData)
    .then((r) => (Array.isArray(r.data) ? r.data[0] : r.data));


// GET /api/member/payments → this member's own payments across all types
// (registration + renewal + booking, newest first), member-scoped from the
// bearer token — no id/params needed, like /api/member/attendances below.
// Response is { member_id, member_code, payments }; each row carries a
// payment_for discriminator ("registration" | "renewal" | "booking").
export const getMemberAllPayments = () =>
  api.get("/api/member/payments").then((r) => (Array.isArray(r.data?.payments) ? r.data.payments : []));

// GET /api/member/payments/registrations → this member's own registration-fee
// payment history, member-scoped from the bearer token (PaymentController::
// myRegistrationPayments). Replaces the earlier workaround of hitting the
// admin-wide GET /api/registration-payments/members, which didn't filter by
// member server-side.
export const getMemberRegistrationPayments = () =>
  api
    .get("/api/member/payments/registrations")
    .then((r) => (Array.isArray(r.data?.payments) ? r.data.payments : []));

// GET /api/member/payments/bookings → this member's own facility booking
// payments, member-scoped from the bearer token (PaymentController::
// myBookingPayments). Replaces the earlier workaround of hitting the
// admin-wide GET /api/payments?payer_id=….
export const getMemberBookingPayments = () =>
  api.get("/api/member/payments/bookings").then((r) => (Array.isArray(r.data?.payments) ? r.data.payments : []));

// GET /api/member/attendances → this member's own attendance scans.
// Member-scoped like /api/member/me — the member is resolved server-side
// from the bearer token (unlike the admin GET /api/attendances in
// coachService.js, which lists everyone's and needs view_attendance — a
// permission no member-facing role, including coaches, actually has).
// Supports the same optional ?date= filter as the admin endpoint.
export const getOwnAttendances = (params = {}) =>
  api.get("/api/member/attendances", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// POST /api/members/{id}/inactivate → admin-only (permission:deactivate_members,
// seeded onto admin/Director Physical Education/Instructor). Only a member
// currently "Active" can be inactivated (422 otherwise); also revokes the
// member's own portal session tokens server-side. Optional `reason` (free
// text, stored for audit, nullable). Returns { message, member } — the
// freshly reloaded formatted Member object.
export const inactivateMember = (id, reason) =>
  api.post(`/api/members/${id}/inactivate`, reason ? { reason } : {}).then((r) => r.data);

// POST /api/members/{id}/reactivate → same permission as inactivate above.
// Only a member currently "Inactive" can be reactivated (422 otherwise).
// Returns { message, member }.
export const reactivateMember = (id) =>
  api.post(`/api/members/${id}/reactivate`).then((r) => r.data);

// GET /api/members/{id}/club-request → permission:request_member_club_change
// (created by the backend's CreateMemberClubChangePermissionSeeder but not
// assigned to any role — tick it on a staff role first). Numeric id only.
// Returns { member_id, member_code, member_type, club_id, club_code,
// pending_request: { id, club_id, club_code, club_name, status, created_at }
// | null }.
export const getMemberClubRequest = (id) =>
  api.get(`/api/members/${id}/club-request`).then((r) => r.data);

// POST /api/members/{id}/club-request — same permission. Coaches 422.
//   { member_type: "Club Student", club_id } → only *requests* the club: a
//     Pending row the club's primary coach accepts/rejects from the portal;
//     the member stays Independent until then (201). 422s if already a Club
//     Student or a request is already pending.
//   { member_type: "Independent" } → immediate: unlinks the club and fails
//     any pending request.
// Returns { message, ...same shape as getMemberClubRequest(), request? }.
export const requestMemberClubChange = (id, payload) =>
  api.post(`/api/members/${id}/club-request`, payload).then((r) => r.data);

// POST /api/member-registration-processes/{processId}/simulate-payment-success
// → test payment gateway. Given a registration PROCESS id, marks its pending
// registration-fee payment as paid and finalizes the member once all required
// steps (e.g. club verification) are done. Optionally pass the amount actually
// paid; it defaults server-side to the process's payable amount. Returns the
// updated process (+ member, once created).
export const simulatePaymentSuccess = (processId, paidAmount) =>
  api
    .post(
      `/api/member-registration-processes/${processId}/simulate-payment-success`,
      paidAmount != null ? { paid_amount: paidAmount } : {}
    )
    .then((r) => r.data);
